import * as yup from 'yup';
import { t } from '@lingui/macro';

import { validatePhoneNumber } from 'utils/phoneFormatter';

export const codeToCountry = (code: string): string => {
  const countryMap: Record<string, string> = {
    '+1': 'US',
    '+52': 'MX',
    '+56': 'CL',
    '+593': 'EC',
    '+81': 'JP',
  };

  return countryMap[code] || 'US';
};

const validationSchema = yup.object().shape(
  {
    id: yup.string(),
    firstName: yup.string().required(t`First name is required`),
    lastName: yup.string().required(t`Last name is required`),
    email: yup
      .string()
      .email(t`Email must be a valid email address`)
      .nullable()
      .when('emailNotificationEnabled', {
        is: true,
        then: (schema) =>
          schema.required(
            t`Email is required when email notifications are enabled`,
          ),
        otherwise: (schema) => schema.nullable(),
      })
      .when('mobilePhone', {
        is: (mobilePhone: string | null | undefined) => !mobilePhone,
        then: (schema) =>
          schema.required(t`Email is required if mobile phone is empty`),
        otherwise: (schema) => schema.nullable(),
      }),
    mobilePhone: yup
      .string()
      .nullable()
      .test(
        'is-valid-phone',
        'Invalid mobile phone number',
        (value, context): boolean =>
          !value ||
          validatePhoneNumber(
            value,
            codeToCountry(context.parent.mobilePhoneCountryCode),
          ),
      )
      .when('smsNotificationEnabled', {
        is: true,
        then: (schema) =>
          schema.required(
            t`Mobile phone is required when SMS notifications are enabled`,
          ),
      })
      .when('email', {
        is: (email: string | null | undefined) => !email,
        then: (schema) =>
          schema.required(t`Mobile phone is required if email is empty`),
        otherwise: (schema) => schema.nullable(),
      }),
    mobilePhoneCountryCode: yup
      .string()
      .nullable()
      .when('mobilePhone', {
        is: (mobilePhone: string | null | undefined) => !!mobilePhone,
        then: (schema) =>
          schema.required(
            t`Country code is required when mobile phone is provided`,
          ),
      }),
    emailNotificationEnabled: yup.boolean().when('smsNotificationEnabled', {
      is: false,
      then: (schema) =>
        schema.oneOf(
          [true],
          t`At least one notification option must be selected`,
        ),
    }),
    smsNotificationEnabled: yup.boolean().when('emailNotificationEnabled', {
      is: false,
      then: (schema) =>
        schema.oneOf(
          [true],
          t`At least one notification option must be selected`,
        ),
    }),
    notes: yup
      .string()
      .nullable()
      .max(200, t`Notes cannot exceed 200 characters`),
    address1: yup.string().nullable(),
    address2: yup.string().nullable(),
    country: yup.string().nullable(),
    state: yup.string().nullable(),
    city: yup.string().nullable(),
    postalCode: yup.string().nullable(),
  },
  [
    ['email', 'mobilePhone'],
    ['emailNotificationEnabled', 'smsNotificationEnabled'],
    ['email', 'emailNotificationEnabled'],
    ['mobilePhone', 'smsNotificationEnabled'],
    ['mobilePhone', 'mobilePhoneCountryCode'],
  ],
);

export default validationSchema;

export type FormValues = yup.InferType<typeof validationSchema>;
